// src/components/Facturacion/FacturacionPanel.js
// Three-column invoice management panel.
// Col 1: manifest list → Col 2: box+client groups → Col 3: invoice work surface.
// Two-step flow: Generar Factura (draft) → Enviar Factura (sent).

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { useTenant } from '../../context/TenantContext';
import { calculateInvoiceDraft, fetchInvoiceRawPayload, computeInvoiceDraft, taxGateBlocked, taxGateSoftOnly } from '../../utils/invoiceCalc';
import { fetchChargeTemplates, recordChargeTemplateUse, deleteInvoiceLineItem } from '../../utils/chargeTemplateHelpers';
import { generateInvoicePDF } from '../../utils/generateInvoicePDF';

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_PILL = {
  draft:   { label: 'Borrador', bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
  sent:    { label: 'Enviada',  bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  paid:    { label: 'Pagada',   bg: '#dcfce7', color: '#15803d', border: '#86efac' },
  partial: { label: 'Parcial',  bg: '#d9f99d', color: '#3f6212', border: '#a3e635' },
  void:    { label: 'Anulada',  bg: '#f3f4f6', color: '#9ca3af', border: '#e5e7eb' },
};

const PAYMENT_METHODS = [
  { value: 'us_bank',        label: 'Banco EE.UU.' },
  { value: 'banco_agricola', label: 'Banco Agrícola' },
  { value: 'zelle',          label: 'Zelle' },
  { value: 'wise',           label: 'Wise' },
  { value: 'cash',           label: 'Efectivo' },
  { value: 'other',          label: 'Otro' },
];

const fmt2   = (n) => `$${Number(n || 0).toFixed(2)}`;
const todayISO = () => new Date().toISOString().slice(0, 10);
const shortId  = (id) => (id || '').split('-').pop() || id;

// ── FacturacionPanel ──────────────────────────────────────────────────────────

// ── Customs pass-through ──────────────────────────────────────────────────────
const TAX_GATE_MESSAGE = 'Falta la declaración. No se puede emitir la factura sin valor declarado.';
const ENCOMIENDA_SIN_VALOR_MESSAGE = 'Ningún paquete de esta encomienda tiene valor declarado. Escanea el recibo o asigna valores del catálogo.';
const ENCOMIENDA_NO_ENCONTRADA_MESSAGE = 'Hay paquetes de esta encomienda que no se encontraron; no se puede calcular su valor declarado.';

// Headline(s) for a blocked invoice, one per tipo that is missing its value —
// a mixed box can be missing both.
const taxGateMessages = (gate = []) => {
  const msgs = [];
  for (const tipo of ['casillero', 'encomienda']) {
    if (gate.includes(`customs_config_missing_${tipo}`)) {
      msgs.push(`No hay configuración aduanal para ${tipo}. Contacta al administrador.`);
    }
  }
  if (gate.includes('declaracion_faltante')) msgs.push(TAX_GATE_MESSAGE);
  if (gate.includes('declaracion_formula_antigua')) {
    msgs.push('La declaración se calculó con la fórmula anterior. Vuelve a confirmarla para recalcular el impuesto por paquete.');
  }
  if (gate.includes('encomienda_sin_valor')) msgs.push(ENCOMIENDA_SIN_VALOR_MESSAGE);
  if (gate.includes('encomienda_paquete_no_encontrado')) msgs.push(ENCOMIENDA_NO_ENCONTRADA_MESSAGE);
  return msgs.length > 0 ? msgs : [TAX_GATE_MESSAGE];
};
const IMPUESTO_LABEL   = 'Impuesto — pasante a la DGA';
const IMPUESTO_PENDIENTE_NOTE = 'Impuesto pendiente de declaración';
// Soft-only gate (see taxGateSoftOnly): a warning, the invoice can still be sent.
const SOFT_GATE_HEADLINE = 'Falta la declaración';
const SOFT_GATE_DETAIL =
  'Completá la declaración del manifiesto en Producción, ingresá el valor declarado o registrá el pasante que cobra el agente aduanal.';

// Manually entered lines: kept across Recalcular, deletable, never computed.
const MANUAL_LINE_TYPES = ['adjustment', 'discount', 'impuesto'];

const lineTypeForAdj = (type) =>
  type === 'descuento' ? 'discount' : type === 'impuesto' ? 'impuesto' : 'adjustment';

const signedAmountForAdj = (type, amount) =>
  type === 'descuento' ? -Math.abs(amount) : Math.abs(amount);

// DAI + IVA already computed on the invoice's lines (0 when none).
const computedTaxTotal = (lines) => {
  let total = 0;
  for (const l of (lines || [])) {
    if (l.line_type === 'dai' || l.line_type === 'iva') total += Number(l.line_total ?? 0) || 0;
  }
  return Math.round(total * 100) / 100;
};

// Ask before stacking a manual pass-through on top of computed tax — that is
// how MAX-INV-2026-00179 was charged $19.02 twice. Returns false to abort.
const confirmManualPasante = (lines) => {
  const existing = computedTaxTotal(lines);
  if (existing <= 0) return true;
  return window.confirm(
    `Esta factura ya tiene impuesto calculado ($${existing.toFixed(2)}). Agregar un pasante manual puede duplicar el cobro.`
  );
};

// Operator overrides belong to one (caja, cliente). usePairScoped keeps one
// value per "boxId:customerId" and reads back only the selected pair's, so
// nothing entered for one client shows up on another. The setter takes a
// value or an updater; reset() forgets every pair (manifest change).
// `empty` must be a stable reference — it feeds effect dependencies.
const NO_LINE_FLAGS = [];
const NO_ADJUSTMENTS = [];

const usePairScoped = (pairKey, empty) => {
  const [byPair, setByPair] = useState({});
  const value = pairKey && pairKey in byPair ? byPair[pairKey] : empty;
  const setValue = (next) => {
    if (!pairKey) return;
    setByPair((prev) => {
      const current = pairKey in prev ? prev[pairKey] : empty;
      return { ...prev, [pairKey]: typeof next === 'function' ? next(current) : next };
    });
  };
  const reset = useCallback(() => setByPair({}), []);
  return [value, setValue, reset];
};

const FacturacionPanel = ({ user }) => {
  const { tenant } = useTenant();
  const currentTenantId = tenant?.id;
  const canGenerate = ['admin', 'manager', 'bodega'].includes(user?.role);

  // ── Tenant info ─────────────────────────────────────────────────────────────
  const [tenantInfo, setTenantInfo] = useState(null);

  // ── Column 1: manifests ─────────────────────────────────────────────────────
  const [manifests,        setManifests]        = useState([]);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [manifestsLoading, setManifestsLoading] = useState(true);

  // ── Column 2: boxes + client groups ────────────────────────────────────────
  const [boxes,        setBoxes]        = useState([]);
  const [clientGroups, setClientGroups] = useState({}); // { box_id: [{ client_id, full_name, weight_lb }] }
  const [invoiceIndex, setInvoiceIndex] = useState({}); // { "boxId:clientId": { id, status, invoice_number } }
  const [boxesLoading, setBoxesLoading] = useState(false);
  const [selectedPair, setSelectedPair] = useState(null); // { boxId, customerId, customerName }
  const [hoveredPair,  setHoveredPair]  = useState(null);

  // Keep a ref so loadCol3 can read invoiceIndex without it being in its deps
  const invoiceIndexRef = useRef({});
  useEffect(() => { invoiceIndexRef.current = invoiceIndex; }, [invoiceIndex]);

  // ── Column 3: invoice work surface ──────────────────────────────────────────
  const [col3Loading,    setCol3Loading]    = useState(false);
  const [invoiceMode,    setInvoiceMode]    = useState(null); // 'A' | 'B' | 'C'
  const [draftResult,    setDraftResult]    = useState(null); // { draft_invoice, line_items, warnings }
  const [savedInvoice,   setSavedInvoice]   = useState(null);
  const [savedLineItems, setSavedLineItems] = useState([]);
  const [savedPayments,  setSavedPayments]  = useState([]);

  // Manual overrides (session-scoped; used in Mode A and Recalcular), each kept
  // per (caja, cliente). Switching client falls back to that client's own
  // declaration, flags and queued lines; switching back restores them.
  const selectedPairKey = selectedPair ? `${selectedPair.boxId}:${selectedPair.customerId}` : null;
  const [manualDeclaredValue, setManualDeclaredValue, resetManualDeclaredValues] = usePairScoped(selectedPairKey, null);
  const [manualLineFlags,     setManualLineFlags,     resetManualLineFlags]      = usePairScoped(selectedPairKey, NO_LINE_FLAGS);

  // Mode A split-phase state
  const [rawPayload,  setRawPayload]  = useState(null); // result of fetchInvoiceRawPayload
  const [isFetching,  setIsFetching]  = useState(false);

  // Charge templates + inline add-line state (shared between Mode A and B)
  const [chargeTemplates,    setChargeTemplates]    = useState([]);
  const [addLineMode,        setAddLineMode]        = useState(null);   // null | 'recargo' | 'descuento'
  const [addLineDescription, setAddLineDescription] = useState('');
  const [addLineAmount,      setAddLineAmount]      = useState('');
  const [addLineNotes,       setAddLineNotes]       = useState('');     // required source for 'impuesto'
  const [addLineSaving,      setAddLineSaving]      = useState(false);
  const [manualAdjustments,  setManualAdjustments, resetManualAdjustments] = usePairScoped(selectedPairKey, NO_ADJUSTMENTS); // Mode A queued before Generar
  const [modeBTaxGate,       setModeBTaxGate]       = useState([]);    // declaration gate reasons for the saved draft
  const [lineDeleteConfirm,  setLineDeleteConfirm]  = useState(null);  // { lineItem } | null

  // ── Mobile responsive ───────────────────────────────────────────────────────
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const isMobile = viewportWidth < 768;
  const [mobileView, setMobileView] = useState('manifests'); // 'manifests' | 'boxes' | 'invoice'

  // ── Action states ───────────────────────────────────────────────────────────
  const [generating, setGenerating] = useState(false);
  const [sending,    setSending]    = useState(false);
  const [recalcing,  setRecalcing]  = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [voiding,    setVoiding]    = useState(false);
  const [paying,     setPaying]     = useState(false);

  // ── Modals ──────────────────────────────────────────────────────────────────
  const [declaredModal,    setDeclaredModal]    = useState(false);
  const [declaredInput,    setDeclaredInput]    = useState('');
  const [flagEditorOpen,   setFlagEditorOpen]   = useState(false);
  const [flagEditorPkgs,   setFlagEditorPkgs]   = useState([]);
  const [flagOverrides,    setFlagOverrides]    = useState({});
  const [flagLoading,      setFlagLoading]      = useState(false);
  const [paymentModal,     setPaymentModal]     = useState(false);
  const [paymentForm,      setPaymentForm]      = useState({ payment_method: 'cash', amount: '', paid_at: todayISO(), reference_number: '', notes: '' });
  const [deleteConfirm,    setDeleteConfirm]    = useState(false);  const [voidConfirm,      setVoidConfirm]      = useState(false);
  const [voidReason,       setVoidReason]       = useState('');

  // ── Toast ───────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Fetch tenant info ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentTenantId) return;
    supabase
      .from('tenants')
      .select('id, company_name, slug')
      .eq('id', currentTenantId)
      .maybeSingle()
      .then(({ data }) => setTenantInfo(data));
  }, [currentTenantId]);

  // ── Fetch charge templates ─────────────────────────────────────────────────
  useEffect(() => {
    if (!currentTenantId) return;
    fetchChargeTemplates(supabase, currentTenantId).then(setChargeTemplates);
  }, [currentTenantId]);

  // ── Fetch manifests ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentTenantId) return;
    const fetch = async () => {
      setManifestsLoading(true);
      const { data, error } = await supabase
        .from('manifests')
        .select('id, status, opened_at, closed_at, total_packages')
        .eq('tenant_id', currentTenantId)
        .order('opened_at', { ascending: false })
        .limit(50);
      if (error) console.error('[Facturacion] manifests:', error);
      setManifests(data || []);
      setManifestsLoading(false);
    };
    fetch();
  }, [currentTenantId]);

  // ── Fetch boxes + client groups + invoice index when manifest changes ────────
  useEffect(() => {
    if (!selectedManifest || !currentTenantId) {
      setBoxes([]);
      setClientGroups({});
      setInvoiceIndex({});
      setSelectedPair(null);
      setInvoiceMode(null);
      setDraftResult(null);
      setRawPayload(null);
      setSavedInvoice(null);
      return;
    }

    const fetchBoxData = async () => {
      setBoxesLoading(true);
      setSelectedPair(null);
      setInvoiceMode(null);
      setDraftResult(null);
      setRawPayload(null);
      setSavedInvoice(null);
      resetManualDeclaredValues();
      resetManualLineFlags();
      resetManualAdjustments();

      const { data: boxData, error: boxErr } = await supabase
        .from('boxes')
        .select('box_id, recipient_name, departamento, municipio, display_sequence')
        .eq('manifest_id', selectedManifest.id)
        .eq('tenant_id', currentTenantId)
        .order('display_sequence', { ascending: true, nullsLast: true })
        .order('created_at', { ascending: true });

      if (boxErr) console.error('[Facturacion] boxes:', boxErr);
      const fetchedBoxes = boxData || [];
      setBoxes(fetchedBoxes);

      if (!fetchedBoxes.length) { setBoxesLoading(false); return; }

      const boxIds = fetchedBoxes.map((b) => b.box_id);

      // box_client_groups
      const { data: gcData } = await supabase
        .from('box_client_groups')
        .select('box_id, client_id, weight_lb')
        .in('box_id', boxIds)
        .eq('tenant_id', currentTenantId);

      const groups = gcData || [];
      const clientIds = [...new Set(groups.map((g) => g.client_id))];

      // customers
      const { data: custData } = clientIds.length
        ? await supabase.from('customers').select('id, full_name').in('id', clientIds).eq('tenant_id', currentTenantId)
        : { data: [] };

      const custMap = {};
      for (const c of (custData || [])) { custMap[c.id] = c.full_name; }

      const cgMap = {};
      for (const g of groups) {
        if (!cgMap[g.box_id]) cgMap[g.box_id] = [];
        cgMap[g.box_id].push({ client_id: g.client_id, full_name: custMap[g.client_id] || g.client_id, weight_lb: g.weight_lb });
      }
      setClientGroups(cgMap);

      // invoice index
      const { data: invData } = await supabase
        .from('invoices')
        .select('id, box_id, customer_id, status, invoice_number')
        .eq('manifest_id', selectedManifest.id)
        .eq('tenant_id', currentTenantId)
        .neq('status', 'void');

      const invIdx = {};
      for (const inv of (invData || [])) {
        invIdx[`${inv.box_id}:${inv.customer_id}`] = { id: inv.id, status: inv.status, invoice_number: inv.invoice_number };
      }
      setInvoiceIndex(invIdx);
      setBoxesLoading(false);
    };

    fetchBoxData();
  }, [selectedManifest, currentTenantId, resetManualDeclaredValues, resetManualLineFlags, resetManualAdjustments]);

  // ── Load Column 3 when pair selection changes ────────────────────────────────
  useEffect(() => {
    if (!selectedPair || !selectedManifest || !currentTenantId) {
      setInvoiceMode(null);
      setDraftResult(null);
      setSavedInvoice(null);
      setSavedLineItems([]);
      setSavedPayments([]);
      return;
    }

    const run = async () => {
      setInvoiceMode(null);
      setDraftResult(null);
      setRawPayload(null);
      setSavedInvoice(null);
      setSavedLineItems([]);
      setSavedPayments([]);

      const inv = invoiceIndexRef.current[`${selectedPair.boxId}:${selectedPair.customerId}`];

      try {
        if (!inv) {
          // Mode A — raw fetch triggered by Effect 1; compute triggered by Effect 2
          setInvoiceMode('A');
        } else if (inv.status === 'draft') {
          setCol3Loading(true);
          // Mode B — draft saved, not yet sent
          const [invRes, liRes] = await Promise.all([
            supabase.from('invoices').select('*').eq('id', inv.id).maybeSingle(),
            supabase.from('invoice_line_items').select('*').eq('invoice_id', inv.id).eq('tenant_id', currentTenantId).order('sort_order'),
          ]);
          setSavedInvoice(invRes.data);
          setSavedLineItems(liRes.data || []);
          setInvoiceMode('B');
        } else {
          // Mode C — sent / paid / partial / void
          setCol3Loading(true);
          const [invRes, liRes, payRes] = await Promise.all([
            supabase.from('invoices').select('*').eq('id', inv.id).maybeSingle(),
            supabase.from('invoice_line_items').select('*').eq('invoice_id', inv.id).eq('tenant_id', currentTenantId).order('sort_order'),
            supabase.from('invoice_payments').select('*').eq('invoice_id', inv.id).eq('tenant_id', currentTenantId).order('paid_at'),
          ]);
          setSavedInvoice(invRes.data);
          setSavedLineItems(liRes.data || []);
          setSavedPayments(payRes.data || []);
          setInvoiceMode('C');
        }
      } catch (err) {
        console.error('[Facturacion] loadCol3:', err);
        showToast(`Error al cargar: ${err.message}`, 'error');
      } finally {
        setCol3Loading(false);
      }
    };

    run();
  }, [selectedPair, selectedManifest, currentTenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effect 1: Mode A raw fetch — fires when (manifest, box, customer) changes ─
  useEffect(() => {
    if (!selectedPair || !selectedManifest || !currentTenantId) {
      setRawPayload(null);
      return;
    }
    // Only run for Mode A (no saved invoice for this pair)
    const inv = invoiceIndexRef.current[`${selectedPair.boxId}:${selectedPair.customerId}`];
    if (inv) { setRawPayload(null); return; }

    let cancelled = false;
    setIsFetching(true);
    (async () => {
      try {
        const result = await fetchInvoiceRawPayload({
          manifestId: selectedManifest.id,
          boxId:      selectedPair.boxId,
          customerId: selectedPair.customerId,
          tenantId:   currentTenantId,
          supabase,
        });
        if (!cancelled) setRawPayload(result);
      } catch (err) {
        if (!cancelled) {
          console.error('[Facturacion] fetchRawPayload:', err);
          showToast(`Error al cargar: ${err.message}`, 'error');
        }
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPair, selectedManifest, currentTenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Effect 2: Mode A compute — re-runs instantly when overrides change ────────
  useEffect(() => {
    if (!rawPayload) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await computeInvoiceDraft({
          raw:                   rawPayload.raw,
          manualDeclaredValueUsd: manualDeclaredValue,
          manualLineFlags,
        });
        if (!cancelled) {
          setDraftResult({
            ...result,
            warnings: [...(rawPayload.fetchWarnings || []), ...(result.warnings || [])],
          });
        }
      } catch (err) {
        if (!cancelled) console.error('[Facturacion] computeInvoiceDraft:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [rawPayload, manualDeclaredValue, manualLineFlags]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Declaration gate for a saved draft (Mode B) ──────────────────────────────
  // Re-reads the declaration — read-only, nothing is written — so a draft
  // generated before its declaration existed is judged on today's data.
  // Returns the gate reasons, or null when the check itself failed.
  const loadSavedInvoiceTaxGate = async () => {
    if (!savedInvoice || !selectedPair || !selectedManifest) return null;
    try {
      const result = await calculateInvoiceDraft({
        manifestId:             selectedManifest.id,
        boxId:                  selectedPair.boxId,
        customerId:             selectedPair.customerId,
        tenantId:               currentTenantId,
        supabase,
        manualDeclaredValueUsd: savedInvoice.manual_declared_value_usd ?? null,
        manualLineFlags,
      });
      return result.draft_invoice.tax_gate || [];
    } catch (err) {
      console.error('[Facturacion] tax gate check:', err);
      return null;
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (invoiceMode !== 'B' || !savedInvoice?.id) { setModeBTaxGate([]); return; }
    let cancelled = false;
    (async () => {
      const gate = await loadSavedInvoiceTaxGate();
      if (!cancelled) setModeBTaxGate(gate ?? ['verificacion_fallida']);
    })();
    return () => { cancelled = true; };
  }, [
    invoiceMode,
    savedInvoice?.id,
    savedInvoice?.manual_declared_value_usd,
    savedInvoice?.declared_value_usd,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // ── TOTAL RECONCILIATION ─────────────────────────────────────────────────────
  // Recompute invoices.total from the invoice_line_items rows.
  // Call after any insert/update/delete on invoice_line_items.
  // balance_due is a GENERATED column (total - amount_paid) — never write it.
  // Returns the refreshed invoice row, or undefined on failure. Never throws.
  const reconcileInvoiceTotal = async (invoiceId) => {
    if (!invoiceId) return;
    const { data: lines, error: fetchErr } = await supabase
      .from('invoice_line_items')
      .select('line_total')
      .eq('invoice_id', invoiceId)
      .eq('tenant_id', currentTenantId);
    if (fetchErr) {
      console.error('[Facturacion] reconcileInvoiceTotal fetch failed:', fetchErr);
      return;
    }
    const newTotal = Math.round((lines || []).reduce((s, l) => s + (Number(l.line_total) || 0), 0) * 100) / 100;
    const { data: updated, error: updErr } = await supabase
      .from('invoices')
      .update({ total: newTotal, updated_at: new Date().toISOString() })
      .eq('id', invoiceId)
      .eq('tenant_id', currentTenantId)
      .select()
      .single();
    if (updErr) {
      console.error('[Facturacion] reconcileInvoiceTotal update failed:', updErr);
      return;
    }
    console.log('[Facturacion] reconciled total:', { invoiceId, newTotal });
    return updated;
  };

  // ── GENERATE DRAFT ───────────────────────────────────────────────────────────
  // Step 1 of 2: creates invoice with status='draft'. Does NOT send.
  const handleGenerateDraft = async () => {
    if (!draftResult || !selectedPair || !selectedManifest) return;
    setGenerating(true);
    try {
      const year   = new Date().getFullYear();
      const prefix = (tenantInfo?.slug || 'INV').toUpperCase().slice(0, 3);

      const { data: invNum, error: rpcErr } = await supabase.rpc('next_invoice_number', {
        p_tenant_id: currentTenantId,
        p_year:      year,
        p_prefix:    prefix,
      });
      if (rpcErr) throw rpcErr;

      const { draft_invoice, line_items } = draftResult;

      const { data: newInv, error: insErr } = await supabase
        .from('invoices')
        .insert({
          tenant_id:                 currentTenantId,
          manifest_id:               selectedManifest.id,
          box_id:                    selectedPair.boxId,
          customer_id:               selectedPair.customerId,
          invoice_number:            invNum,
          status:                    'draft',
          currency:                  'USD',
          total_weight_lbs:          draft_invoice.total_weight_lbs,
          declared_value_usd:        draft_invoice.declared_value_usd,
          declared_value_source:     draft_invoice.declared_value_source,
          manual_declared_value_usd: manualDeclaredValue,
          subtotal:                  draft_invoice.subtotal,
          surcharge_total:           draft_invoice.surcharge_total,
          impuesto_total:            draft_invoice.impuesto_total,
          // Placeholder. reconcileInvoiceTotal() sets the real value once the line
          // items exist — total is always a reconciled value, never a preview value.
          total:                     0,
          manual_declared_value_used: draft_invoice.manual_declared_value_used,
          created_by:                user.id,
        })
        .select()
        .single();
      if (insErr) throw insErr;

      // Build line items: computed lines + any queued manual adjustments
      const adjLines = manualAdjustments.map((adj, i) => {
        const lt = signedAmountForAdj(adj.type, adj.amount);
        return {
          invoice_id:       newInv.id,
          tenant_id:        currentTenantId,
          line_type:        lineTypeForAdj(adj.type),
          description:      adj.description,
          notes:            adj.notes || null,
          quantity:         1,
          unit_price:       lt,
          line_total:       lt,
          pricing_rule_key: null,
          source_type:      'manual',
          source_id:        null,
          sort_order:       line_items.length + i,
        };
      });

      const liPayload = [
        ...line_items.map((li) => ({
          invoice_id:           newInv.id,
          tenant_id:            currentTenantId,
          line_type:            li.line_type,
          description:          li.description,
          quantity:             li.quantity,
          unit_price:           li.unit_price,
          line_total:           li.line_total,
          pricing_rule_key:     li.pricing_rule_key,
          source_type:          li.source_type,
          source_id:            li.source_id,
          source_snapshot_hash: li.source_snapshot_hash,
          sort_order:           li.sort_order,
        })),
        ...adjLines,
      ];

      const { error: liErr } = await supabase.from('invoice_line_items').insert(liPayload);
      if (liErr) {
        await supabase.from('invoices').delete().eq('id', newInv.id);
        throw liErr;
      }

      // Fold the just-inserted lines (computed + queued manual adjustments) into total.
      const reconciledInv = await reconcileInvoiceTotal(newInv.id);

      // Record recargo template usage (fire-and-forget)
      for (const adj of manualAdjustments) {
        if (adj.type === 'recargo') {
          recordChargeTemplateUse(supabase, currentTenantId, adj.description);
        }
      }

      // Transition to Mode B
      const allSavedLines = [...line_items, ...adjLines.map((l, i) => ({ ...l, id: undefined, sort_order: line_items.length + i }))];
      const key = `${selectedPair.boxId}:${selectedPair.customerId}`;
      setInvoiceIndex((prev) => ({ ...prev, [key]: { id: newInv.id, status: 'draft', invoice_number: invNum } }));
      setSavedInvoice(reconciledInv || newInv);
      setSavedLineItems(allSavedLines);
      setSavedPayments([]);
      setDraftResult(null);
      setManualAdjustments([]);
      setInvoiceMode('B');
      showToast(`Borrador ${invNum} generado`);
    } catch (err) {
      console.error('[Facturacion] generateDraft:', err);
      showToast(`Error al generar: ${err.message}`, 'error');
    } finally {
      setGenerating(false);
    }
  };

  // ── SEND INVOICE ─────────────────────────────────────────────────────────────
  // Step 2 of 2: transitions draft → sent.
  // A soft-only gate (missing value, see taxGateSoftOnly) does not stop the
  // send: the invoice is flagged impuesto_pendiente and the tax goes on a
  // later invoice. Any other gate reason blocks.
  const handleSendInvoice = async () => {
    if (!savedInvoice) return;
    setSending(true);
    try {
      // Declaration gate: never set issued_at on an invoice whose tax was not
      // assessed, unless a manual pass-through ('impuesto') line replaces it.
      const gate = await loadSavedInvoiceTaxGate();
      if (gate === null) {
        setModeBTaxGate(['verificacion_fallida']);
        showToast('No se pudo verificar la declaración. Intenta de nuevo.', 'error');
        return;
      }
      setModeBTaxGate(gate);
      const blocked = taxGateBlocked(gate, savedLineItems);
      const sendPending = blocked && taxGateSoftOnly(gate);
      if (blocked && !sendPending) {
        showToast(taxGateMessages(gate)[0], 'error');
        return;
      }

      const now = new Date().toISOString();
      const patch = { status: 'sent', issued_at: now };
      if (sendPending) {
        patch.impuesto_pendiente        = true;
        patch.impuesto_pendiente_reason = gate[0];
        patch.impuesto_pendiente_at     = now;
        patch.notes = savedInvoice.notes
          ? `${savedInvoice.notes} — ${IMPUESTO_PENDIENTE_NOTE}`
          : IMPUESTO_PENDIENTE_NOTE;
      }

      // Only a draft can be issued. RLS and the status guard both fail
      // silently (zero rows), so an empty result is an error.
      const { data: rows, error } = await supabase
        .from('invoices')
        .update(patch)
        .eq('id', savedInvoice.id)
        .eq('tenant_id', currentTenantId)
        .eq('status', 'draft')
        .select();
      if (error) throw error;
      if (!rows || rows.length === 0) {
        showToast('No se pudo emitir la factura: ya no es un borrador o no tenés permiso.', 'error');
        return;
      }
      const updated = rows[0];

      // Auto-generate PDF on send. Failure does not roll back the send.
      let pdfOk = true;
      try {
        await generateInvoicePDF(savedInvoice.id, supabase);
      } catch (pdfErr) {
        console.error('[Facturacion] PDF auto-generate failed:', pdfErr);
        pdfOk = false;
      }

      const key = `${selectedPair.boxId}:${selectedPair.customerId}`;
      setInvoiceIndex((prev) => ({ ...prev, [key]: { ...prev[key], status: 'sent' } }));
      setSavedInvoice(updated);
      setInvoiceMode('C');
      showToast(
        pdfOk
          ? `Factura ${updated.invoice_number} enviada`
          : 'Factura enviada. Error al generar PDF — usa el botón Descargar PDF.'
      );
    } catch (err) {
      console.error('[Facturacion] sendInvoice:', err);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setSending(false);
    }
  };

  // ── DOWNLOAD PDF (all Mode C statuses, all roles) ────────────────────────────
  const handleDownloadPDF = async () => {
    if (!savedInvoice?.id) return;
    try {
      await generateInvoicePDF(savedInvoice.id, supabase);
    } catch (err) {
      console.error('[Facturacion] PDF download failed:', err);
      showToast(`Error al generar PDF: ${err.message || err}`, 'error');
    }
  };

  // ── RECALCULATE DRAFT ─────────────────────────────────────────────────────────
  const handleRecalcular = async (overrideDeclaredVal) => {
    if (!savedInvoice || !selectedPair) return;
    setRecalcing(true);
    try {
      const declVal = overrideDeclaredVal !== undefined ? overrideDeclaredVal : (savedInvoice.manual_declared_value_usd ?? manualDeclaredValue);
      const result = await calculateInvoiceDraft({
        manifestId:           selectedManifest.id,
        boxId:                selectedPair.boxId,
        customerId:           selectedPair.customerId,
        tenantId:             currentTenantId,
        supabase,
        manualDeclaredValueUsd: declVal,
        manualLineFlags,
      });
      const { draft_invoice, line_items } = result;

      // Preserve user-entered adjustments/discounts across the recalc: delete only
      // the machine-computed lines, never the manual ones.
      const { data: existingLines, error: fetchErr } = await supabase
        .from('invoice_line_items')
        .select('id, line_type')
        .eq('invoice_id', savedInvoice.id)
        .eq('tenant_id', currentTenantId);
      if (fetchErr) throw fetchErr;

      const isManual = (l) => MANUAL_LINE_TYPES.includes(l.line_type);
      const preservedLines  = (existingLines || []).filter(isManual);
      const computedLineIds = (existingLines || []).filter((l) => !isManual(l)).map((l) => l.id);

      if (computedLineIds.length > 0) {
        const { error: delErr } = await supabase
          .from('invoice_line_items')
          .delete()
          .in('id', computedLineIds)
          .eq('tenant_id', currentTenantId);
        if (delErr) throw delErr;
      }

      const liPayload = line_items.map((li) => ({
        invoice_id: savedInvoice.id, tenant_id: currentTenantId,
        line_type: li.line_type, description: li.description,
        quantity: li.quantity, unit_price: li.unit_price, line_total: li.line_total,
        pricing_rule_key: li.pricing_rule_key, source_type: li.source_type,
        source_id: li.source_id, source_snapshot_hash: li.source_snapshot_hash,
        sort_order: li.sort_order,
      }));
      const { error: liErr } = await supabase.from('invoice_line_items').insert(liPayload);
      if (liErr) throw liErr;

      // total is intentionally absent — reconcileInvoiceTotal() derives it below from
      // the freshly inserted computed lines plus the preserved manual lines.
      const { error: updErr } = await supabase
        .from('invoices')
        .update({
          total_weight_lbs:          draft_invoice.total_weight_lbs,
          declared_value_usd:        draft_invoice.declared_value_usd,
          declared_value_source:     draft_invoice.declared_value_source,
          manual_declared_value_usd: declVal,
          subtotal:                  draft_invoice.subtotal,
          surcharge_total:           draft_invoice.surcharge_total,
          impuesto_total:            draft_invoice.impuesto_total,
          manual_declared_value_used: draft_invoice.manual_declared_value_used,
        })
        .eq('id', savedInvoice.id)
        .eq('tenant_id', currentTenantId);
      if (updErr) throw updErr;

      const reconciledInv = await reconcileInvoiceTotal(savedInvoice.id);

      // Re-read the lines rather than trusting `line_items`, which omits the
      // preserved manual rows.
      const { data: refreshedLines } = await supabase
        .from('invoice_line_items')
        .select('*')
        .eq('invoice_id', savedInvoice.id)
        .eq('tenant_id', currentTenantId)
        .order('sort_order');

      if (reconciledInv) setSavedInvoice(reconciledInv);
      setSavedLineItems(refreshedLines || []);
      setDraftResult(result);
      console.log('[Facturacion] recalcular done:', {
        invoiceId: savedInvoice.id,
        preserved: preservedLines.length,
        recomputed: computedLineIds.length,
      });
      showToast('Borrador recalculado');
    } catch (err) {
      console.error('[Facturacion] recalcular:', err);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setRecalcing(false);
    }
  };

  // ── DELETE DRAFT ──────────────────────────────────────────────────────────────
  const handleDeleteDraft = async () => {
    if (!savedInvoice) return;
    setDeleting(true);
    try {
      const { data: deletedRows, error: delErr } = await supabase
        .from('invoices')
        .delete()
        .eq('id', savedInvoice.id)
        .eq('tenant_id', currentTenantId)
        .eq('status', 'draft')
        .select();

      if (delErr) throw delErr;
      if (!deletedRows || deletedRows.length === 0) {
        throw new Error(
          'La factura no pudo eliminarse. Es posible que ya haya sido enviada o no tienes permisos. Recarga la página.'
        );
      }

      const key = `${selectedPair.boxId}:${selectedPair.customerId}`;
      setInvoiceIndex((prev) => { const n = { ...prev }; delete n[key]; return n; });
      setSavedInvoice(null);
      setSavedLineItems([]);
      setInvoiceMode(null);
      setDeleteConfirm(false);
      showToast('Borrador eliminado');
    } catch (err) {
      console.error('[Facturacion] deleteDraft:', err);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── VOID INVOICE ──────────────────────────────────────────────────────────────
  const handleVoidInvoice = async () => {
    if (!savedInvoice || !voidReason.trim()) return;
    setVoiding(true);
    try {
      const { data: updated, error } = await supabase
        .from('invoices')
        .update({ status: 'void', voided_at: new Date().toISOString(), voided_reason: voidReason.trim(), voided_by: user.id })
        .eq('id', savedInvoice.id)
        .eq('tenant_id', currentTenantId)
        .select()
        .single();
      if (error) throw error;
      const key = `${selectedPair.boxId}:${selectedPair.customerId}`;
      setInvoiceIndex((prev) => ({ ...prev, [key]: { ...prev[key], status: 'void' } }));
      setSavedInvoice(updated);
      setVoidConfirm(false);
      setVoidReason('');
      showToast('Factura anulada');
    } catch (err) {
      console.error('[Facturacion] void:', err);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setVoiding(false);
    }
  };

  // ── RECORD PAYMENT ────────────────────────────────────────────────────────────
  const handleRecordPayment = async () => {
    if (!savedInvoice || !paymentForm.amount) return;
    setPaying(true);
    try {
      const { error: insErr } = await supabase.from('invoice_payments').insert({
        invoice_id:       savedInvoice.id,
        tenant_id:        currentTenantId,
        payment_method:   paymentForm.payment_method,
        amount:           parseFloat(paymentForm.amount),
        paid_at:          paymentForm.paid_at,
        reference_number: paymentForm.reference_number || null,
        notes:            paymentForm.notes || null,
        recorded_by:      user.id,
      });
      if (insErr) throw insErr;
      // Reload invoice + payments — DB trigger updates amount_paid and status
      const [invRes, payRes] = await Promise.all([
        supabase.from('invoices').select('*').eq('id', savedInvoice.id).maybeSingle(),
        supabase.from('invoice_payments').select('*').eq('invoice_id', savedInvoice.id).eq('tenant_id', currentTenantId).order('paid_at'),
      ]);
      setSavedInvoice(invRes.data);
      setSavedPayments(payRes.data || []);
      if (invRes.data?.status !== savedInvoice.status) {
        const key = `${selectedPair.boxId}:${selectedPair.customerId}`;
        setInvoiceIndex((prev) => ({ ...prev, [key]: { ...prev[key], status: invRes.data.status } }));
      }
      setPaymentModal(false);
      setPaymentForm({ payment_method: 'cash', amount: '', paid_at: todayISO(), reference_number: '', notes: '' });
      showToast('Pago registrado');
    } catch (err) {
      console.error('[Facturacion] recordPayment:', err);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setPaying(false);
    }
  };

  // ── DECLARED VALUE MODAL ─────────────────────────────────────────────────────
  const handleDeclaredValueSave = async () => {
    const newVal = parseFloat(declaredInput) || null;
    setManualDeclaredValue(newVal);
    setDeclaredModal(false);

    if (invoiceMode === 'A') {
      // Effect 2 auto-recomputes when manualDeclaredValue changes — no network call
    } else if (invoiceMode === 'B') {
      await handleRecalcular(newVal);
    }
  };

  // ── FLAG EDITOR ───────────────────────────────────────────────────────────────
  const openFlagEditor = async () => {
    setFlagLoading(true);
    setFlagEditorOpen(true);
    const { data } = await supabase
      .from('box_packages')
      .select('package_ref_id, package_type')
      .eq('box_id', selectedPair.boxId)
      .eq('client_id', selectedPair.customerId)
      .eq('tenant_id', currentTenantId);
    setFlagEditorPkgs(data || []);
    // Pre-populate overrides from current flags
    const init = {};
    for (const f of manualLineFlags) { init[f.package_ref_id] = f.treat_as; }
    setFlagOverrides(init);
    setFlagLoading(false);
  };

  const applyFlagOverrides = async () => {
    const newFlags = Object.entries(flagOverrides)
      .filter(([, v]) => v === 'documento' || v === 'celular')
      .map(([k, v]) => ({ package_ref_id: k, treat_as: v }));
    setManualLineFlags(newFlags);
    setFlagEditorOpen(false);

    if (invoiceMode === 'A') {
      // Effect 2 auto-recomputes when manualLineFlags changes — no network call
    } else if (invoiceMode === 'B') {
      setCol3Loading(true);
      try {
        await handleRecalcular(savedInvoice?.manual_declared_value_usd ?? manualDeclaredValue);
      } catch (err) {
        showToast(`Error: ${err.message}`, 'error');
      } finally {
        setCol3Loading(false);
      }
    }
  };

  // ── RENDER HELPERS ────────────────────────────────────────────────────────────

  // ── INLINE ADD LINE UI ────────────────────────────────────────────────────────
  // onSubmit(description, amount, type) — caller decides whether to queue or persist.
  const renderAddLineUI = (onSubmit) => {
    if (!canGenerate) return null;
    const btnBase = { fontSize: '12px', padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 600, border: 'none' };
    const MODE_META = {
      recargo:   { label: '+ Recargo',     color: '#1d4ed8', submitBg: '#2563eb' },
      descuento: { label: '− Descuento',   color: '#6b7280', submitBg: '#6b7280' },
      impuesto:  { label: IMPUESTO_LABEL,  color: '#b45309', submitBg: '#d97706' },
    };
    const meta = MODE_META[addLineMode] || MODE_META.descuento;
    const isImpuesto = addLineMode === 'impuesto';
    const open = (mode) => {
      setAddLineMode(mode);
      setAddLineDescription(mode === 'impuesto' ? IMPUESTO_LABEL : '');
      setAddLineAmount('');
      setAddLineNotes('');
    };
    const close = () => { setAddLineMode(null); setAddLineDescription(''); setAddLineAmount(''); setAddLineNotes(''); };
    // A manual pass-through must name where the figure came from.
    const cannotSubmit = addLineSaving || !addLineDescription.trim() || !addLineAmount || (isImpuesto && !addLineNotes.trim());
    const submit = () => onSubmit(addLineDescription.trim(), parseFloat(addLineAmount) || 0, addLineMode, isImpuesto ? addLineNotes.trim() : null);

    const field = isMobile
      ? { fontSize: '13px', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '6px', width: '100%', boxSizing: 'border-box' }
      : { fontSize: '12px', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: '6px', flex: '1', minWidth: '140px' };
    const amountField = isMobile ? { ...field, textAlign: 'right' } : { ...field, flex: 'none', minWidth: 0, width: '80px', textAlign: 'right' };
    const btnSize = isMobile ? { flex: 1, padding: '9px 12px' } : {};

    return (
      <div style={{ marginTop: '12px', padding: '10px 0', borderTop: '1px dashed var(--border)' }}>
        {addLineMode === null ? (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => open('recargo')}
              style={{ ...btnBase, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #93c5fd' }}>
              + Recargo
            </button>
            <button type="button" onClick={() => open('descuento')}
              style={{ ...btnBase, background: '#f9fafb', color: '#6b7280', border: '1px solid #d1d5db' }}>
              − Descuento
            </button>
            <button type="button" onClick={() => open('impuesto')}
              style={{ ...btnBase, background: '#fffbeb', color: '#b45309', border: '1px solid #fcd34d' }}>
              + {IMPUESTO_LABEL}
            </button>
          </div>
        ) : (
          <div style={isMobile
            ? { display: 'flex', flexDirection: 'column', gap: '8px' }
            : { display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: meta.color, minWidth: isMobile ? undefined : '72px' }}>
              {meta.label}
            </span>
            {addLineMode === 'recargo' ? (
              <>
                <input list="charge-templates-list"
                  value={addLineDescription}
                  onChange={(e) => setAddLineDescription(e.target.value)}
                  placeholder="Descripción..."
                  style={field}
                />
                <datalist id="charge-templates-list">
                  {chargeTemplates.map((t) => (
                    <option key={t.id} value={t.description} />
                  ))}
                </datalist>
              </>
            ) : (
              <input
                value={addLineDescription}
                onChange={(e) => setAddLineDescription(e.target.value)}
                placeholder="Descripción..."
                style={field}
              />
            )}
            {isImpuesto && (
              <input
                value={addLineNotes}
                onChange={(e) => setAddLineNotes(e.target.value)}
                placeholder="Fuente (obligatorio): ej. liquidación del agente aduanal"
                aria-label="Fuente del monto"
                style={{ ...field, borderColor: addLineNotes.trim() ? 'var(--border)' : '#fcd34d' }}
              />
            )}
            <input
              type="number" min="0" step="0.01"
              value={addLineAmount}
              onChange={(e) => setAddLineAmount(e.target.value)}
              placeholder="0.00"
              style={amountField}
            />
            <div style={{ display: 'flex', gap: isMobile ? '8px' : '6px' }}>
              <button type="button" disabled={cannotSubmit} onClick={submit}
                style={{ ...btnBase, ...btnSize, background: meta.submitBg, color: '#fff', opacity: cannotSubmit ? 0.5 : 1 }}>
                {addLineSaving ? 'Agregando...' : 'Agregar'}
              </button>
              <button type="button" onClick={close}
                style={{ ...btnBase, ...btnSize, background: 'transparent', border: '1px solid var(--border)', color: '#6b7280' }}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── ADD LINE TO SAVED INVOICE (Mode B) ────────────────────────────────────────
  const handleAddLineToSavedInvoice = async (description, amount, type, notes) => {
    if (!savedInvoice || !selectedPair || !currentTenantId) return;
    if (type === 'impuesto' && !confirmManualPasante(savedLineItems)) return;
    setAddLineSaving(true);
    const lineTotal = signedAmountForAdj(type, amount);
    try {
      const sortMax = savedLineItems.reduce((m, l) => Math.max(m, l.sort_order ?? 0), 0);
      const { data: newLine, error: lineErr } = await supabase
        .from('invoice_line_items')
        .insert({
          invoice_id:       savedInvoice.id,
          tenant_id:        currentTenantId,
          line_type:        lineTypeForAdj(type),
          description:      description,
          notes:            notes || null,
          quantity:         1,
          unit_price:       lineTotal,
          line_total:       lineTotal,
          pricing_rule_key: null,
          source_type:      'manual',
          source_id:        null,
          sort_order:       sortMax + 1,
        })
        .select()
        .single();
      if (lineErr) throw lineErr;

      const reconciledInv = await reconcileInvoiceTotal(savedInvoice.id);

      if (type === 'recargo') {
        recordChargeTemplateUse(supabase, currentTenantId, description); // fire-and-forget
      }

      setSavedLineItems((prev) => [...prev, newLine]);
      if (reconciledInv) setSavedInvoice(reconciledInv);
      setAddLineMode(null);
      setAddLineDescription('');
      setAddLineAmount('');
      setAddLineNotes('');
    } catch (err) {
      console.error('[Facturacion] addLine:', err);
      showToast(`Error: ${err.message}`, 'error');
    } finally {
      setAddLineSaving(false);
    }
  };

  // ── DELETE SAVED LINE (Mode B) ────────────────────────────────────────────────
  const handleDeleteSavedLine = async (lineItem) => {
    if (!savedInvoice || !currentTenantId) return;
    const ok = await deleteInvoiceLineItem(supabase, savedInvoice.id, lineItem.id, currentTenantId);
    if (!ok) { showToast('Error al eliminar la línea', 'error'); return; }

    const reconciledInv = await reconcileInvoiceTotal(savedInvoice.id);

    setSavedLineItems((prev) => prev.filter((l) => l.id !== lineItem.id));
    if (reconciledInv) setSavedInvoice(reconciledInv);
    setLineDeleteConfirm(null);
    showToast('Línea eliminada');
  };

  const renderStatusPill = (status) => {
    if (!status) return <span style={{ fontSize: '11px', color: '#9ca3af' }}>• Pendiente</span>;
    const p = STATUS_PILL[status];
    if (!p) return null;
    return (
      <span style={{
        fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '99px',
        background: p.bg, color: p.color, border: `1px solid ${p.border}`,
        textDecoration: status === 'void' ? 'line-through' : 'none',
      }}>{p.label}</span>
    );
  };

  const renderInvoiceLines = (lineItems, invoiceSummary = null, { onDeleteLine } = {}) => {
    const freightLines    = lineItems.filter((l) => l.line_type?.startsWith('freight_'));
    const surchargeLines  = lineItems.filter((l) => l.line_type?.startsWith('surcharge_'));
    const daiLine         = lineItems.find((l) => l.line_type === 'dai');
    const ivaLine         = lineItems.find((l) => l.line_type === 'iva');
    const impuestoLines   = lineItems.filter((l) => l.line_type === 'impuesto');
    const adjustmentLines = lineItems.filter((l) => l.line_type === 'adjustment');
    const discountLines   = lineItems.filter((l) => l.line_type === 'discount');
    const subtotal   = freightLines.reduce((s, l) => s + (l.line_total || 0), 0);
    const surcharge  = surchargeLines.reduce((s, l) => s + (l.line_total || 0), 0);
    const dai        = invoiceSummary?.dai_total ?? daiLine?.line_total ?? 0;
    const iva        = invoiceSummary?.iva_total ?? ivaLine?.line_total ?? 0;
    const impuesto   = impuestoLines.reduce((s, l) => s + (Number(l.line_total ?? 0) || 0), 0);
    const recargos   = adjustmentLines.reduce((s, l) => s + (l.line_total || 0), 0);
    const descuentos = discountLines.reduce((s, l) => s + (l.line_total || 0), 0); // negative values
    const total = subtotal + surcharge + dai + iva + impuesto + recargos + descuentos;
    const isDeletable = (li) => MANUAL_LINE_TYPES.includes(li.line_type);

    const thStyle = { textAlign: 'left', padding: '6px 8px', fontWeight: 700, fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)' };
    const tdStyle = { padding: '7px 8px', fontSize: '13px', borderBottom: '1px solid var(--border)' };
    const numStyle = { ...tdStyle, textAlign: 'right', fontFamily: 'monospace' };

    return (
      <div style={{ marginTop: '16px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--bg)' }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: onDeleteLine ? '46%' : '50%' }}>Descripción</th>
              <th style={{ ...thStyle, textAlign: 'right', width: '12%' }}>Cant.</th>
              <th style={{ ...thStyle, textAlign: 'right', width: '18%' }}>Precio Unit.</th>
              <th style={{ ...thStyle, textAlign: 'right', width: '20%' }}>Total</th>
              {onDeleteLine && <th style={{ ...thStyle, width: '4%' }} />}
            </tr>
          </thead>
          <tbody>
            {lineItems.map((li, i) => (
              <tr key={li.id || li._key || i} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)' }}>
                <td style={tdStyle}>{li.description}</td>
                <td style={numStyle}>{li.quantity}</td>
                <td style={numStyle}>{fmt2(li.unit_price)}</td>
                <td style={numStyle}>{fmt2(li.line_total)}</td>
                {onDeleteLine && (
                  <td style={{ ...tdStyle, textAlign: 'center', padding: '4px' }}>
                    {isDeletable(li) ? (
                      <button type="button" onClick={() => onDeleteLine(li)}
                        title="Eliminar línea"
                        style={{ fontSize: '11px', lineHeight: 1, padding: '2px 5px', border: '1px solid #fca5a5', borderRadius: '4px', background: 'transparent', color: '#dc2626', cursor: 'pointer' }}>
                        ×
                      </button>
                    ) : null}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {/* Totals */}
        <div style={{ marginTop: '8px', borderTop: '2px solid var(--border)', paddingTop: '8px' }}>
          {[
            { label: 'Subtotal fletes',      val: subtotal,   show: true },
            { label: 'Recargo',              val: surcharge,  show: surcharge > 0 },
            { label: 'DAI',                  val: dai,        show: dai > 0 },
            { label: 'IVA',                  val: iva,        show: iva > 0 },
            // Manual pass-through to the DGA (and legacy pre-DAI/IVA-split invoices)
            { label: IMPUESTO_LABEL,         val: impuesto,   show: impuesto > 0 },
            { label: 'Recargos',             val: recargos,   show: recargos > 0 },
            { label: 'Descuentos',           val: descuentos, show: descuentos < 0 },
          ].filter((r) => r.show).map((r) => (
            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 8px', fontSize: '13px', color: '#6b7280' }}>
              <span>{r.label}</span>
              <span style={{ fontFamily: 'monospace' }}>{fmt2(r.val)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 8px 4px', fontSize: '16px', fontWeight: 800, borderTop: '1px solid var(--border)', marginTop: '4px', letterSpacing: '0.03em' }}>
            <span>TOTAL</span>
            <span style={{ fontFamily: 'monospace' }}>{fmt2(total)}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderWarnings = (warnings = []) => {
    if (!warnings.length) return null;
    const WARN_LABELS = {
      casillero_fob_missing: 'Valor FOB del casillero no detectado — DAI e IVA pueden ser $0. Edite el valor declarado si conoce el monto.',
      declared_value_zero:   'Valor declarado es $0 — verifica con el cliente',
      declaration_drafts_missing:       'El manifiesto no tiene declaración.',
      package_not_in_declaration:       'Hay paquetes de esta caja que no están en la declaración.',
      declaration_drafts_not_confirmed: 'La declaración del manifiesto no está confirmada.',
      customs_config_incomplete:        'Faltan tasas en la configuración aduanal — no se puede calcular el impuesto.',
      customs_config_missing:           'Falta la configuración aduanal para el tipo de esta caja — no se puede calcular el impuesto.',
    };
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
        {warnings.map((w) => (
          <span key={w} style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '99px', background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
            ⚠ {WARN_LABELS[w] || w}
          </span>
        ))}
      </div>
    );
  };

  // Banner for the declaration gate. Offers the ways out: enter the declared
  // value (the tax is then computed), or record the broker's figure as a
  // manual pass-through line. A soft-only gate (missing value) is an amber
  // warning and the invoice can still be sent (impuesto pendiente). Any other
  // reason stays a red hard lock.
  const renderTaxGateBanner = (gate, lines, currentDeclaredValue) => {
    if (!taxGateBlocked(gate, lines)) return null;
    const soft = taxGateSoftOnly(gate);
    const tone = soft
      ? { bg: '#fffbeb', border: '#fcd34d', fg: '#92400e', icon: '⚠️', role: 'status' }
      : { bg: '#fef2f2', border: '#fca5a5', fg: '#991b1b', icon: '⛔', role: 'alert' };
    const headlines = soft ? [SOFT_GATE_HEADLINE] : taxGateMessages(gate);
    const detail = soft
      ? SOFT_GATE_DETAIL
      : gate.includes('customs_config_incomplete')
      ? 'Además faltan tasas en la configuración aduanal.'
      : gate.some((g) => g.startsWith('customs_config_missing_'))
        ? 'Mientras tanto podés registrar el pasante que cobra el agente aduanal.'
      : gate.includes('verificacion_fallida')
        ? 'No se pudo verificar la declaración; recargá la factura.'
        : gate.includes('declaracion_faltante')
          ? 'Completá la declaración del manifiesto en Producción, ingresá el valor declarado o registrá el pasante que cobra el agente aduanal.'
          : 'También podés ingresar el valor declarado o registrar el pasante que cobra el agente aduanal.';
    const actionBtn = { fontSize: '12px', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 };
    return (
      <div role={tone.role} style={{ padding: '12px 14px', marginBottom: '12px', borderRadius: '8px', background: tone.bg, border: `1px solid ${tone.border}`, color: tone.fg }}>
        {headlines.map((m) => (
          <div key={m} style={{ fontSize: '13px', fontWeight: 800 }}>{tone.icon} {m}</div>
        ))}
        <div style={{ fontSize: '12px', marginTop: '4px' }}>{detail}</div>
        {canGenerate && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
            <button type="button"
              onClick={() => { setDeclaredInput(currentDeclaredValue || ''); setDeclaredModal(true); }}
              style={{ ...actionBtn, background: tone.fg, color: '#fff', border: 'none' }}>
              Ingresar valor declarado
            </button>
            <button type="button"
              onClick={() => { setAddLineMode('impuesto'); setAddLineDescription(IMPUESTO_LABEL); setAddLineAmount(''); setAddLineNotes(''); }}
              style={{ ...actionBtn, background: 'transparent', color: tone.fg, border: `1px solid ${tone.fg}` }}>
              Registrar pasante manual
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderInvoiceHeader = (badge, badgeStyle, inv, customerInfo) => (
    <div style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
        <div>
          <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--text)' }}>{tenantInfo?.company_name || 'Empresa'}</div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
            {new Date().toLocaleDateString('es-SV', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
        <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px', ...badgeStyle }}>{badge}</span>
      </div>
      {inv?.invoice_number && (
        <div style={{ fontSize: '20px', fontWeight: 800, color: 'var(--blue)', fontFamily: 'monospace', marginBottom: '8px' }}>
          {inv.invoice_number}
        </div>
      )}
      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
        <div><span style={{ fontWeight: 700 }}>Cliente:</span> {customerInfo?.full_name || selectedPair?.customerName}</div>
        <div><span style={{ fontWeight: 700 }}>ID:</span> {selectedPair?.customerId}</div>
        <div><span style={{ fontWeight: 700 }}>Manifiesto:</span> {selectedManifest?.id}</div>
        <div><span style={{ fontWeight: 700 }}>Caja:</span> {shortId(selectedPair?.boxId)}</div>
        <div><span style={{ fontWeight: 700 }}>Peso:</span> {(inv?.total_weight_lbs ?? draftResult?.draft_invoice?.total_weight_lbs ?? 0).toFixed(1)} lb</div>
      </div>
    </div>
  );

  // ── COLUMN 3 MODES ────────────────────────────────────────────────────────────

  const renderModeA = () => {
    const { draft_invoice, line_items, warnings } = draftResult;
    const hasFobWarning = warnings?.includes('casillero_fob_missing');

    // Merge computed lines with locally queued adjustments for display
    const adjAsLines = manualAdjustments.map((adj, i) => {
      const lt = signedAmountForAdj(adj.type, adj.amount);
      return { _key: `local-${i}`, _localIdx: i, line_type: lineTypeForAdj(adj.type), description: adj.description, notes: adj.notes || null, quantity: 1, unit_price: lt, line_total: lt };
    });
    const allLineItems = [...line_items, ...adjAsLines];

    const onDeleteLocalAdj = (li) =>
      setManualAdjustments((prev) => prev.filter((_, i) => i !== li._localIdx));

    const onAddLocalAdj = (description, amount, type, notes) => {
      if (type === 'impuesto' && !confirmManualPasante(line_items)) return;
      setManualAdjustments((prev) => [...prev, { description, amount, type, notes }]);
      setAddLineMode(null);
      setAddLineDescription('');
      setAddLineAmount('');
      setAddLineNotes('');
    };

    return (
      <div>
        {renderInvoiceHeader(
          'FACTURA — BORRADOR (NO GUARDADA)',
          { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' },
          null, null
        )}
        {renderWarnings(warnings)}
        {renderTaxGateBanner(draft_invoice.tax_gate || [], allLineItems, draft_invoice.declared_value_usd)}
        {/* Declared value row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', marginBottom: '4px' }}>
          <span style={{ fontSize: '13px', color: '#6b7280' }}>Valor FOB:</span>
          <span style={{ fontSize: '14px', fontWeight: 700, fontFamily: 'monospace', color: hasFobWarning ? '#92400e' : 'var(--text)', background: hasFobWarning ? '#fef3c7' : 'transparent', padding: hasFobWarning ? '2px 6px' : '0', borderRadius: '4px' }}>
            {fmt2(draft_invoice.declared_value_usd)}
          </span>
          {draft_invoice.declared_value_source === 'manual' && (
            <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '99px', background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>manual</span>
          )}
          {draft_invoice.declared_value_source === 'none' && (
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>sin declaración</span>
          )}
          {canGenerate && (
            <button type="button" onClick={() => { setDeclaredInput(draft_invoice.declared_value_usd || ''); setDeclaredModal(true); }}
              style={{ fontSize: '11px', padding: '3px 8px', border: '1px solid var(--border)', borderRadius: '6px', background: 'transparent', cursor: 'pointer', color: 'var(--text)' }}>
              Editar valor declarado
            </button>
          )}
        </div>
        {renderInvoiceLines(allLineItems, draft_invoice, { onDeleteLine: canGenerate ? onDeleteLocalAdj : undefined })}
        {renderAddLineUI(onAddLocalAdj)}
        {/* Buttons */}
        {canGenerate && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
            <button type="button" onClick={handleGenerateDraft} disabled={generating}
              style={{ padding: '9px 20px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer', opacity: generating ? 0.6 : 1 }}>
              {generating ? 'Generando...' : 'Generar Factura'}
            </button>
            <button type="button" onClick={openFlagEditor}
              style={{ padding: '9px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: 'var(--text)' }}>
              Marcar paquetes como sobre/celular
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderModeB = () => {
    const hasFobWarning = savedInvoice?.declared_value_usd === 0;
    const sendBlocked = taxGateBlocked(modeBTaxGate, savedLineItems) && !taxGateSoftOnly(modeBTaxGate);
    return (
      <div>
        {renderInvoiceHeader(
          'BORRADOR',
          { background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' },
          savedInvoice, null
        )}
        {renderTaxGateBanner(modeBTaxGate, savedLineItems, savedInvoice?.manual_declared_value_usd)}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', marginBottom: '4px' }}>
          <span style={{ fontSize: '13px', color: '#6b7280' }}>Valor FOB:</span>
          <span style={{ fontSize: '14px', fontWeight: 700, fontFamily: 'monospace', color: hasFobWarning ? '#92400e' : 'var(--text)', background: hasFobWarning ? '#fef3c7' : 'transparent', padding: hasFobWarning ? '2px 6px' : '0', borderRadius: '4px' }}>
            {fmt2(savedInvoice?.declared_value_usd)}
          </span>
          {savedInvoice?.declared_value_source === 'manual' && (
            <span style={{ fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '99px', background: '#dbeafe', color: '#1d4ed8', border: '1px solid #bfdbfe' }}>manual</span>
          )}
          {savedInvoice?.declared_value_source === 'none' && (
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>sin declaración</span>
          )}
        </div>
        {renderInvoiceLines(savedLineItems, savedInvoice, { onDeleteLine: canGenerate ? (li) => setLineDeleteConfirm({ lineItem: li }) : undefined })}
        {renderAddLineUI(handleAddLineToSavedInvoice)}
        {/* Buttons */}
        {canGenerate && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
            <button type="button" onClick={() => handleSendInvoice()} disabled={sending || sendBlocked}
              title={sendBlocked ? taxGateMessages(modeBTaxGate).join(' ') : undefined}
              style={{ padding: '9px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: sendBlocked ? 'not-allowed' : 'pointer', opacity: (sending || sendBlocked) ? 0.5 : 1 }}>
              {sending ? 'Enviando...' : 'Enviar Factura'}
            </button>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <button type="button" onClick={() => handleRecalcular()} disabled={recalcing}
                style={{ padding: '9px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: 'var(--text)', opacity: recalcing ? 0.6 : 1 }}>
                {recalcing ? 'Recalculando...' : 'Recalcular Borrador'}
              </button>
              <span style={{ fontSize: '10px', color: '#9ca3af', paddingLeft: '2px' }}>
                Se eliminarán ajustes manuales existentes.
              </span>
            </div>
            <button type="button" onClick={() => { setDeclaredInput(savedInvoice?.manual_declared_value_usd || ''); setDeclaredModal(true); }}
              style={{ padding: '9px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: 'var(--text)' }}>
              Editar valor declarado
            </button>
            <button type="button" onClick={() => setDeleteConfirm(true)}
              style={{ padding: '9px 16px', background: 'transparent', border: '1px solid #fca5a5', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: '#dc2626', marginLeft: 'auto' }}>
              Eliminar Borrador
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderModeC = () => {
    const st = savedInvoice?.status;
    const pill = STATUS_PILL[st] || STATUS_PILL.sent;
    const isVoid = st === 'void';
    const badgeStyle = { background: pill.bg, color: pill.color, border: `1px solid ${pill.border}` };
    return (
      <div>
        {renderInvoiceHeader(pill.label.toUpperCase(), badgeStyle, savedInvoice, null)}
        {isVoid && savedInvoice?.voided_reason && (
          <div style={{ padding: '8px 12px', background: '#f9fafb', border: '1px solid var(--border)', borderRadius: '6px', marginBottom: '12px', fontSize: '12px', color: '#6b7280' }}>
            <strong>Motivo de anulación:</strong> {savedInvoice.voided_reason}
          </div>
        )}
        <div style={{ display: 'flex', gap: '24px', fontSize: '13px', color: '#6b7280', marginBottom: '8px', flexWrap: 'wrap' }}>
          <div><strong>Total:</strong> <span style={{ fontFamily: 'monospace', color: 'var(--text)', fontWeight: 700 }}>{fmt2(savedInvoice?.total)}</span></div>
          {savedInvoice?.amount_paid > 0 && <div><strong>Pagado:</strong> <span style={{ fontFamily: 'monospace', color: '#15803d', fontWeight: 700 }}>{fmt2(savedInvoice.amount_paid)}</span></div>}
          {savedInvoice?.balance_due > 0 && <div><strong>Saldo:</strong> <span style={{ fontFamily: 'monospace', color: '#c2410c', fontWeight: 700 }}>{fmt2(savedInvoice.balance_due)}</span></div>}
        </div>
        {renderInvoiceLines(savedLineItems, savedInvoice)}
        {/* Payments */}
        {savedPayments.length > 0 && (
          <div style={{ marginTop: '20px' }}>
            <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '8px', color: 'var(--text)' }}>Pagos registrados</div>
            {savedPayments.map((p) => (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f9fafb', borderRadius: '6px', marginBottom: '4px', fontSize: '12px' }}>
                <div>
                  <span style={{ fontWeight: 600 }}>{PAYMENT_METHODS.find((m) => m.value === p.payment_method)?.label || p.payment_method}</span>
                  {p.reference_number && <span style={{ color: '#6b7280', marginLeft: '8px' }}>Ref: {p.reference_number}</span>}
                  <span style={{ color: '#6b7280', marginLeft: '8px' }}>{p.paid_at}</span>
                </div>
                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{fmt2(p.amount)}</span>
              </div>
            ))}
          </div>
        )}
        {/* Buttons */}
        {canGenerate && !isVoid && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
            <button type="button" onClick={() => { setPaymentForm((f) => ({ ...f, amount: savedInvoice?.balance_due || '', paid_at: todayISO() })); setPaymentModal(true); }}
              style={{ padding: '9px 20px', background: '#15803d', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
              Registrar Pago
            </button>
            <button type="button" onClick={() => setVoidConfirm(true)}
              style={{ padding: '9px 16px', background: 'transparent', border: '1px solid #fca5a5', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: '#dc2626', marginLeft: 'auto' }}>
              Anular Factura
            </button>
          </div>
        )}
        {/* Descargar PDF — visible to all roles and all statuses (including void) */}
        <div style={{ marginTop: '12px' }}>
          <button type="button" onClick={handleDownloadPDF}
            style={{ padding: '9px 16px', background: 'transparent', border: '1px solid var(--border)', borderRadius: '8px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: 'var(--text)' }}>
            Descargar PDF
          </button>
        </div>
      </div>
    );
  };

  // ── MAIN RENDER ───────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', position: 'relative' }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
          padding: '12px 20px', borderRadius: '10px', fontWeight: 600, fontSize: '13px',
          background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
          color:      toast.type === 'error' ? '#dc2626'  : '#15803d',
          border:     `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>{toast.msg}</div>
      )}

      {/* Inner flex row containing the three columns */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

      {/* ── COLUMN 1: Manifests ─────────────────────────────────────────────── */}
      {(!isMobile || mobileView === 'manifests') && (
      <div style={isMobile
        ? { width: '100%', overflowY: 'auto', background: 'var(--bg)' }
        : { width: '280px', flexShrink: 0, borderRight: '1px solid var(--border)', overflowY: 'auto', background: 'var(--bg)' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: '13px', color: 'var(--text)' }}>
          Manifiestos
        </div>
        {manifestsLoading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Cargando...</div>
        ) : manifests.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Sin manifiestos</div>
        ) : (
          manifests.map((m) => {
            const isSelected = selectedManifest?.id === m.id;
            return (
              <div key={m.id} onClick={() => { setSelectedManifest(m); if (isMobile) setMobileView('boxes'); }}
                style={{
                  padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                  borderLeft: isSelected ? '3px solid var(--blue)' : '3px solid transparent',
                  background: isSelected ? 'rgba(var(--blue-rgb, 37,99,235),0.05)' : 'transparent',
                }}>
                <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text)' }}>{m.id}</div>
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                  {m.opened_at ? new Date(m.opened_at).toLocaleDateString('es-SV') : '—'}
                  {m.total_packages != null && ` · ${m.total_packages} paquetes`}
                </div>
                <div style={{ marginTop: '4px' }}>{renderStatusPill(m.status === 'CLOSED' ? null : undefined)}</div>
              </div>
            );
          })
        )}
      </div>
      )}

      {/* ── COLUMN 2: Boxes + client groups ─────────────────────────────────── */}
      {(!isMobile || mobileView === 'boxes') && (
      <div style={isMobile
        ? { width: '100%', overflowY: 'auto', background: 'var(--bg)' }
        : { width: '340px', flexShrink: 0, borderRight: '1px solid var(--border)', overflowY: 'auto', background: 'var(--bg)' }}>
        {/* Mobile back navigation */}
        {isMobile && (
          <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button type="button" onClick={() => setMobileView('manifests')}
              style={{ fontSize: '13px', fontWeight: 600, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--blue)', padding: 0 }}>
              ← Atrás
            </button>
            {selectedManifest && (
              <span style={{ fontSize: '12px', color: '#6b7280' }}>Manifiesto {selectedManifest.id}</span>
            )}
          </div>
        )}
        {!selectedManifest ? (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Selecciona un manifiesto</div>
        ) : boxesLoading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Cargando cajas...</div>
        ) : boxes.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Sin cajas</div>
        ) : (
          <>
            {!isMobile && (
              <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: '12px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {selectedManifest.id}
              </div>
            )}
            {boxes.map((box) => {
              const clients = clientGroups[box.box_id] || [];
              return (
                <div key={box.box_id} style={{ borderBottom: '1px solid var(--border)' }}>
                  {/* Box header */}
                  <div style={{ padding: '10px 16px', background: '#f9fafb' }}>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--text)' }}>CAJA {shortId(box.box_id)}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>
                      {[box.municipio, box.departamento].filter(Boolean).join(', ') || '—'}
                    </div>
                  </div>
                  {/* Client sub-rows */}
                  {clients.map((cl) => {
                    const key = `${box.box_id}:${cl.client_id}`;
                    const inv = invoiceIndex[key];
                    const isSelected = selectedPair?.boxId === box.box_id && selectedPair?.customerId === cl.client_id;
                    const isHovered  = hoveredPair === key;
                    return (
                      <div key={key}
                        onClick={() => { setSelectedPair({ boxId: box.box_id, customerId: cl.client_id, customerName: cl.full_name }); if (isMobile) setMobileView('invoice'); }}
                        onMouseEnter={() => setHoveredPair(key)}
                        onMouseLeave={() => setHoveredPair(null)}
                        style={{
                          padding: '8px 16px 8px 24px', cursor: 'pointer',
                          borderLeft: isSelected ? '3px solid var(--blue)' : '3px solid transparent',
                          background: isSelected ? 'rgba(37,99,235,0.06)' : isHovered ? 'rgba(0,0,0,0.03)' : 'transparent',
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '12px', color: 'var(--text)' }}>
                            {cl.client_id} — {cl.full_name}
                          </div>
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>{cl.weight_lb?.toFixed(1)} lb</div>
                        </div>
                        <div>{renderStatusPill(inv?.status || null)}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        )}
      </div>
      )}

      {/* ── COLUMN 3: Invoice work surface ──────────────────────────────────── */}
      {(!isMobile || mobileView === 'invoice') && (
      <div style={isMobile
        ? { width: '100%', overflowY: 'auto', padding: '16px' }
        : { flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
        {/* Mobile back navigation */}
        {isMobile && (
          <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button type="button" onClick={() => setMobileView('boxes')}
              style={{ fontSize: '13px', fontWeight: 600, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--blue)', padding: 0 }}>
              ← Atrás
            </button>
            {selectedPair && (
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                {selectedPair.customerName} · {selectedManifest?.id}
              </span>
            )}
          </div>
        )}
        {!selectedPair ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '14px' }}>
            Selecciona un cliente para ver o generar factura
          </div>
        ) : (col3Loading || isFetching) ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '14px' }}>
            Calculando...
          </div>
        ) : (
          <>
            {invoiceMode === 'A' && draftResult ? renderModeA()
              : invoiceMode === 'B' && savedInvoice   ? renderModeB()
              : invoiceMode === 'C' && savedInvoice   ? renderModeC()
              : null}
          </>
        )}
      </div>
      )}

      </div>{/* end inner flex row */}

      {/* ── MODAL: Eliminar línea ──────────────────────────────────────────────── */}
      {lineDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="tile" style={{ width: '360px', padding: '24px', borderRadius: '12px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 700 }}>¿Eliminar esta línea?</h3>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#6b7280' }}>
              {lineDeleteConfirm.lineItem.description} — {fmt2(lineDeleteConfirm.lineItem.line_total)}
            </p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setLineDeleteConfirm(null)}
                style={{ padding: '8px 16px', border: '1px solid var(--border)', borderRadius: '8px', background: 'transparent', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                Cancelar
              </button>
              <button type="button" onClick={() => handleDeleteSavedLine(lineDeleteConfirm.lineItem)}
                style={{ padding: '8px 16px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Editar valor declarado ────────────────────────────────────── */}
      {declaredModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="tile" style={{ width: '360px', padding: '24px', borderRadius: '12px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700 }}>Editar valor declarado</h3>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: '6px' }}>VALOR DECLARADO (USD)</label>
            <input type="number" min="0" step="0.01" value={declaredInput} onChange={(e) => setDeclaredInput(e.target.value)}
              placeholder="0.00"
              style={{ width: '100%', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'monospace' }} />
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDeclaredModal(false)} style={{ padding: '8px 16px', border: '1px solid var(--border)', borderRadius: '8px', background: 'transparent', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Cancelar</button>
              <button type="button" onClick={handleDeclaredValueSave} style={{ padding: '8px 20px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>Aplicar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Flag editor ───────────────────────────────────────────────── */}
      {flagEditorOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="tile" style={{ width: '480px', padding: '24px', borderRadius: '12px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 700 }}>Tipo de paquete</h3>
            <p style={{ margin: '0 0 16px', fontSize: '12px', color: '#6b7280' }}>Asigna tipo especial por paquete. Si no hay cambio, déjalo como está.</p>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {flagLoading ? <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>Cargando...</div> : flagEditorPkgs.map((pkg) => (
                <div key={pkg.package_ref_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '12px' }}>
                    <span style={{ fontWeight: 600 }}>{pkg.package_type}</span>
                    <span style={{ color: '#9ca3af', marginLeft: '8px', fontFamily: 'monospace', fontSize: '10px' }}>{pkg.package_ref_id.slice(-8)}</span>
                  </div>
                  <select value={flagOverrides[pkg.package_ref_id] || 'default'}
                    onChange={(e) => setFlagOverrides((prev) => ({ ...prev, [pkg.package_ref_id]: e.target.value }))}
                    style={{ padding: '5px 8px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px', background: 'var(--bg)', color: 'var(--text)' }}>
                    <option value="default">Sin cambio ({pkg.package_type})</option>
                    <option value="documento">Documento / sobre</option>
                    <option value="celular">Celular</option>
                  </select>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setFlagEditorOpen(false)} style={{ padding: '8px 16px', border: '1px solid var(--border)', borderRadius: '8px', background: 'transparent', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Cancelar</button>
              <button type="button" onClick={applyFlagOverrides} style={{ padding: '8px 20px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>Aplicar y recalcular</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Registrar Pago ────────────────────────────────────────────── */}
      {paymentModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="tile" style={{ width: '440px', padding: '24px', borderRadius: '12px' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '15px', fontWeight: 700 }}>Registrar Pago</h3>
            {[
              { label: 'MÉTODO DE PAGO', field: 'payment_method', type: 'select' },
              { label: 'MONTO (USD)',     field: 'amount',         type: 'number' },
              { label: 'FECHA',           field: 'paid_at',        type: 'date' },
              { label: 'REFERENCIA',      field: 'reference_number', type: 'text' },
              { label: 'NOTAS',           field: 'notes',          type: 'textarea' },
            ].map(({ label, field, type }) => (
              <div key={field} style={{ marginBottom: '14px' }}>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</label>
                {type === 'select' ? (
                  <select value={paymentForm[field]} onChange={(e) => setPaymentForm((f) => ({ ...f, [field]: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }}>
                    {PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                ) : type === 'textarea' ? (
                  <textarea value={paymentForm[field]} onChange={(e) => setPaymentForm((f) => ({ ...f, [field]: e.target.value }))} rows={2}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', background: 'var(--bg)', color: 'var(--text)' }} />
                ) : (
                  <input type={type} value={paymentForm[field]} onChange={(e) => setPaymentForm((f) => ({ ...f, [field]: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', background: 'var(--bg)', color: 'var(--text)' }} />
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setPaymentModal(false)} style={{ padding: '8px 16px', border: '1px solid var(--border)', borderRadius: '8px', background: 'transparent', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Cancelar</button>
              <button type="button" onClick={handleRecordPayment} disabled={paying || !paymentForm.amount}
                style={{ padding: '8px 20px', background: '#15803d', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700, opacity: paying ? 0.6 : 1 }}>
                {paying ? 'Registrando...' : 'Registrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Eliminar Borrador confirm ─────────────────────────────────── */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="tile" style={{ width: '380px', padding: '24px', borderRadius: '12px' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: '15px', fontWeight: 700 }}>¿Eliminar borrador?</h3>
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#6b7280' }}>Esta acción es permanente. El número de factura quedará sin usar.</p>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDeleteConfirm(false)} style={{ padding: '8px 16px', border: '1px solid var(--border)', borderRadius: '8px', background: 'transparent', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Cancelar</button>
              <button type="button" onClick={handleDeleteDraft} disabled={deleting}
                style={{ padding: '8px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700, opacity: deleting ? 0.6 : 1 }}>
                {deleting ? 'Eliminando...' : 'Sí, eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Anular Factura confirm ────────────────────────────────────── */}
      {voidConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="tile" style={{ width: '400px', padding: '24px', borderRadius: '12px' }}>
            <h3 style={{ margin: '0 0 10px', fontSize: '15px', fontWeight: 700 }}>Anular factura</h3>
            <label style={{ fontSize: '12px', fontWeight: 700, color: '#6b7280', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Motivo *</label>
            <textarea value={voidReason} onChange={(e) => setVoidReason(e.target.value)} rows={3} placeholder="Describe el motivo de anulación..."
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', resize: 'vertical', boxSizing: 'border-box', background: 'var(--bg)', color: 'var(--text)' }} />
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setVoidConfirm(false); setVoidReason(''); }} style={{ padding: '8px 16px', border: '1px solid var(--border)', borderRadius: '8px', background: 'transparent', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>Cancelar</button>
              <button type="button" onClick={handleVoidInvoice} disabled={voiding || !voidReason.trim()}
                style={{ padding: '8px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 700, opacity: (voiding || !voidReason.trim()) ? 0.6 : 1 }}>
                {voiding ? 'Anulando...' : 'Confirmar anulación'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default FacturacionPanel;
